import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Package, User, Calendar } from "lucide-react";

export default function ProfilePage() {
  const { user, token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(response.data);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchOrders();
  }, [token]);

  const confirmPayment = async (orderId) => {
    try {
      await axios.put(
        `${API}/orders/${orderId}/confirm-payment`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders(prev => prev.map(o => 
        o.id === orderId ? { ...o, payment_status: "confirmed" } : o
      ));
      toast.success("Payment marked as sent!");
    } catch (error) {
      toast.error("Failed to update payment status");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  return (
    <div className="min-h-screen pt-32 pb-24 px-4 sm:px-6 lg:px-8" data-testid="profile-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <span className="label-text text-[#39FF14] text-sm tracking-[0.3em]">MY ACCOUNT</span>
          <h1 className="mt-2 text-4xl heading text-[#EDEDED]">Profile</h1>
        </div>

        <Tabs defaultValue="orders" className="space-y-8">
          <TabsList className="bg-[#0A0A0A] border border-[#222] p-1">
            <TabsTrigger 
              value="orders" 
              className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black label-text"
              data-testid="tab-orders"
            >
              <Package size={18} className="mr-2" />
              Orders
            </TabsTrigger>
            <TabsTrigger 
              value="account" 
              className="data-[state=active]:bg-[#39FF14] data-[state=active]:text-black label-text"
              data-testid="tab-account"
            >
              <User size={18} className="mr-2" />
              Account
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-6">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-32 skeleton rounded-lg"></div>
                ))}
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-16 bg-[#0A0A0A] border border-[#222]">
                <Package size={48} className="mx-auto text-[#333] mb-4" />
                <p className="text-[#888]">No orders yet</p>
              </div>
            ) : (
              orders.map((order) => (
                <div
                  key={order.id}
                  className="bg-[#0A0A0A] border border-[#222] p-6"
                  data-testid={`order-${order.id}`}
                >
                  {/* Order Header */}
                  <div className="flex flex-wrap items-start justify-between gap-4 pb-4 border-b border-[#222]">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[#888] text-sm">Order ID:</span>
                        <span className="text-[#EDEDED] font-mono">{order.id.slice(0, 8)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[#888] text-sm">
                        <Calendar size={14} />
                        <span>{formatDate(order.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`status-badge ${order.payment_status}`}>
                        {order.payment_status === "confirmed" ? "Payment Sent" : "Awaiting Payment"}
                      </span>
                      <span className="text-[#39FF14] text-xl heading">${order.total.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="py-4 space-y-3">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex items-center gap-4">
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-16 h-16 object-cover"
                        />
                        <div className="flex-1">
                          <p className="text-[#EDEDED]">{item.name}</p>
                          <p className="text-[#888] text-sm">
                            {item.size && `Size: ${item.size}`}
                            {item.size && item.color && " • "}
                            {item.color && `Color: ${item.color}`}
                            {" • "}Qty: {item.quantity}
                          </p>
                        </div>
                        <p className="text-[#888]">${(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>

                  {/* Shipping & Payment */}
                  <div className="pt-4 border-t border-[#222] flex flex-wrap justify-between gap-4">
                    <div>
                      <p className="text-[#888] text-sm mb-1">Shipping to:</p>
                      <p className="text-[#EDEDED] text-sm">
                        {order.shipping_address}, {order.shipping_city}, {order.shipping_state} {order.shipping_zip}
                      </p>
                    </div>
                    <div>
                      <p className="text-[#888] text-sm mb-1">Payment Method:</p>
                      <p className="text-[#EDEDED] text-sm capitalize">{order.payment_method}</p>
                    </div>
                    {order.payment_status === "pending" && (
                      <button
                        onClick={() => confirmPayment(order.id)}
                        className="btn-skew bg-[#39FF14] text-black px-6 py-2 font-bold uppercase text-sm"
                        data-testid={`confirm-payment-${order.id}`}
                      >
                        <span>I've Sent Payment</span>
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          {/* Account Tab */}
          <TabsContent value="account">
            <div className="bg-[#0A0A0A] border border-[#222] p-6">
              <h3 className="text-xl heading text-[#EDEDED] mb-6">Account Details</h3>
              <div className="space-y-6">
                <div>
                  <label className="text-[#888] label-text text-sm">Name</label>
                  <p className="mt-1 text-[#EDEDED] text-lg">{user?.name}</p>
                </div>
                <div>
                  <label className="text-[#888] label-text text-sm">Email</label>
                  <p className="mt-1 text-[#EDEDED] text-lg">{user?.email}</p>
                </div>
                <div>
                  <label className="text-[#888] label-text text-sm">Member Since</label>
                  <p className="mt-1 text-[#EDEDED] text-lg">
                    {user?.created_at ? formatDate(user.created_at) : "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
